import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  Toolbar,
  Grid,
  Stack,
  Avatar,
  Chip,
  Card,
  CardContent,
  Fade,
  Alert,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Download,
  Payment,
  CheckCircle,
  Share,
  Print,
  Email,
  Business,
  Schedule,
  AttachMoney,
  Receipt,
  ContentCopy,
} from '@mui/icons-material';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import HomeHeader from '../../../../Components/HomeHeader';
import HomeFooter from '../../../../Components/HomeFooter';
import API from '../../../../API';

const CreatorInvoicePage = () => {
  const { id } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        setLoading(true);
        const data = await API.creatorConnect.getInvoice(id);
        setInvoice(data);
      } catch (error) {
        console.error('Error fetching invoice:', error);
        setError('Failed to load invoice. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchInvoice();
    }
  }, [id]);

  const handleDownloadPDF = async () => {
    setPdfGenerating(true);
    try {
      const input = document.getElementById('invoice-content');
      const canvas = await html2canvas(input, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

      pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice_${invoice.po_number || id}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
    } finally {
      setPdfGenerating(false);
    }
  };

  const handleCopyInvoiceNumber = () => {
    navigator.clipboard.writeText(invoice.po_number || invoice.id);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'success';
      case 'approved': return 'info';
      case 'pending': return 'warning';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'Paid';
      case 'approved': return 'Approved';
      case 'pending': return 'Pending Payment';
      case 'cancelled': return 'Cancelled';
      default: return status || 'Unknown';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <Box textAlign="center">
          <CircularProgress size={60} thickness={4} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading invoice...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error || !invoice) {
    return (
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          <Typography variant="h6">Invoice Not Found</Typography>
          <Typography>{error || 'The requested invoice could not be found.'}</Typography>
        </Alert>
      </Box>
    );
  }

  // Updated to use unified model field names
  const invoiceAmount = invoice.amount_due || invoice.amount || 0;
  const createdDate = invoice.created_at || invoice.invoice_created_at;
  const dueDate = invoice.payout_date;
  const invoiceStatus = invoice.status || invoice.invoice_status;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HomeHeader />
      <Toolbar />

      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        {/* Action Bar */}
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <CardContent>
            <Stack 
              direction={{ xs: 'column', sm: 'row' }} 
              justifyContent="space-between" 
              alignItems={{ xs: 'stretch', sm: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography variant="h5" fontWeight={600} gutterBottom>
                  Invoice #{invoice.po_number || invoice.id}
                </Typography>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip 
                    label={getStatusText(invoiceStatus)}
                    color={getStatusColor(invoiceStatus)}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Created {formatDate(createdDate)}
                  </Typography>
                </Stack>
              </Box>
              
              <Stack direction="row" spacing={1}>
                <Tooltip title="Copy Invoice Number">
                  <IconButton onClick={handleCopyInvoiceNumber} size="small">
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
                
                <Button
                  variant="outlined"
                  startIcon={<Share />}
                  size="small"
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  Share
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={pdfGenerating ? <CircularProgress size={16} /> : <Download />}
                  onClick={handleDownloadPDF}
                  disabled={pdfGenerating}
                  size="small"
                >
                  {pdfGenerating ? 'Generating...' : 'Download PDF'}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {/* Invoice Content */}
        <Fade in={true} timeout={800}>
          <Paper
            id="invoice-content"
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 4,
              }}
            >
              <Grid container spacing={4} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Stack direction="row" spacing={3} alignItems="center">
                    {invoice.creator_info?.pfphref && (
                      <Avatar
                        src={invoice.creator_info.pfphref}
                        sx={{
                          width: 80,
                          height: 80,
                          border: '3px solid white',
                          boxShadow: 3,
                        }}
                      />
                    )}
                    <Box>
                      <Typography variant="h3" fontWeight={800} gutterBottom>
                        INVOICE
                      </Typography>
                      <Typography variant="h6" sx={{ opacity: 0.9 }}>
                        From: {invoice.creator_info?.creator_id || 'Creator'}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ opacity: 0.8 }}>
                        Pangea Omninational Corporation
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
                
                <Grid item xs={12} md={4} textAlign={{ xs: 'left', md: 'right' }}>
                  <Typography variant="h2" fontWeight={700}>
                    ${invoiceAmount.toFixed(2)}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                    Total Amount
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Details Section */}
            <Box sx={{ p: 4 }}>
              <Grid container spacing={4}>
                {/* Invoice Information */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom color="primary">
                        <Business sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Bill To
                      </Typography>
                      <Stack spacing={1}>
                        <Typography variant="body1" fontWeight={500}>
                          {invoice.client_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {invoice.client_email}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Invoice Metadata */}
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight={600} gutterBottom color="primary">
                        <Receipt sx={{ mr: 1, verticalAlign: 'middle' }} />
                        Invoice Details
                      </Typography>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">Invoice #:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {invoice.po_number || invoice.id}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">Date Issued:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(createdDate)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">Due Date:</Typography>
                          <Typography variant="body2" fontWeight={500}>
                            {formatDate(dueDate)}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2">Status:</Typography>
                          <Chip 
                            label={getStatusText(invoiceStatus)}
                            color={getStatusColor(invoiceStatus)}
                            size="small"
                          />
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Line Items */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Services
                </Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead sx={{ bgcolor: 'grey.50' }}>
                      <TableRow>
                        <TableCell><strong>Description</strong></TableCell>
                        <TableCell align="center"><strong>Quantity</strong></TableCell>
                        <TableCell align="right"><strong>Rate</strong></TableCell>
                        <TableCell align="right"><strong>Amount</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <Typography variant="body1">
                            Creative Services - {invoice.po_number}
                          </Typography>
                          {invoice.notes && (
                            <Typography variant="body2" color="text.secondary">
                              {invoice.notes}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">1</TableCell>
                        <TableCell align="right">${invoiceAmount.toFixed(2)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body1" fontWeight={600}>
                            ${invoiceAmount.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              {/* Summary */}
              <Box sx={{ mt: 4 }}>
                <Grid container justifyContent="flex-end">
                  <Grid item xs={12} sm={6} md={4}>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={2}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="body1">Subtotal:</Typography>
                            <Typography variant="body1">${invoiceAmount.toFixed(2)}</Typography>
                          </Stack>
                          
                          <Divider />
                          
                          <Stack direction="row" justifyContent="space-between">
                            <Typography variant="h6" fontWeight={600}>Total:</Typography>
                            <Typography variant="h6" fontWeight={600} color="primary">
                              ${invoiceAmount.toFixed(2)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Payment Information */}
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Payment Information
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Bank Details
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="body2"><strong>Account Holder:</strong> Houston Alexander Roa</Typography>
                          <Typography variant="body2"><strong>Bank:</strong> Prosperity Bank</Typography>
                          <Typography variant="body2"><strong>Account #:</strong> 217841714</Typography>
                          <Typography variant="body2"><strong>Routing #:</strong> 113122655</Typography>
                          <Typography variant="body2"><strong>SWIFT:</strong> PROYUS44</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <Card variant="outlined" sx={{ borderRadius: 2, height: '100%' }}>
                      <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                          Bank Address
                        </Typography>
                        <Stack spacing={0.5}>
                          <Typography variant="body2">Prosperity Bank</Typography>
                          <Typography variant="body2">2117 N. Fry Road</Typography>
                          <Typography variant="body2">Katy, TX 77449</Typography>
                          <Typography variant="body2">United States</Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>

              {/* Payment CTA */}
              {invoiceStatus?.toLowerCase() === 'pending' && (
                <Box sx={{ mt: 4 }}>
                  <Card
                    sx={{
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      borderRadius: 3,
                    }}
                  >
                    <CardContent sx={{ textAlign: 'center', py: 4 }}>
                      <CheckCircle sx={{ fontSize: 48, mb: 2, opacity: 0.9 }} />
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        Secure Payment Portal
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                        Pay securely with Credit Card, PayPal, or Bank Transfer
                      </Typography>
                      <Stack 
                        direction={{ xs: 'column', sm: 'row' }} 
                        spacing={2} 
                        justifyContent="center"
                      >
                        <Button
                          variant="contained"
                          size="large"
                          startIcon={<Payment />}
                          href={invoice.invoice_link}
                          sx={{
                            bgcolor: 'white',
                            color: 'primary.main',
                            fontWeight: 600,
                            px: 4,
                            '&:hover': { bgcolor: 'grey.100' },
                          }}
                        >
                          Pay ${invoiceAmount.toFixed(2)} Now
                        </Button>
                        <Button
                          variant="outlined"
                          size="large"
                          startIcon={<Email />}
                          sx={{
                            borderColor: 'white',
                            color: 'white',
                            '&:hover': { 
                              borderColor: 'white', 
                              bgcolor: 'rgba(255,255,255,0.1)' 
                            },
                          }}
                        >
                          Contact Support
                        </Button>
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              )}

              {/* Footer */}
              <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  Questions about this invoice? Contact us at support@blitzpay.pro
                </Typography>
                <Typography variant="caption" color="text.secondary" textAlign="center" display="block" sx={{ mt: 1 }}>
                  Pangea Omninational Corporation • 8724 Cavell Lane, Houston, TX 77055
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>

      <HomeFooter />
    </Box>
  );
};

export default CreatorInvoicePage;